import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";

/* An image that prints on documents — the clinic logo, a doctor's signature.
 * Uploads as soon as a file is picked (the server stores it under a fixed
 * name, so there is no draft to save) and previews what is stored now. The
 * preview is fetched as a blob because the image route needs the auth header,
 * which a bare <img src> would not send. */
export function ImageUpload({ fetchUrl, uploadUrl, label, hint, testId }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const load = useCallback(() => {
    api.get(fetchUrl, { responseType: "blob" })
      .then((r) => setPreview(URL.createObjectURL(r.data)))
      .catch(() => setPreview(null));   // 404 = nothing uploaded yet
  }, [fetchUrl]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex h-20 w-40 items-center justify-center rounded-lg border border-dashed border-navy/20 bg-cream/40">
        {preview
          ? <img src={preview} alt={label} className="max-h-[4.5rem] max-w-[9.5rem] object-contain" />
          : <span className="text-xs text-muted-foreground">No {label.toLowerCase()}</span>}
      </div>
      <div>
        <input ref={input} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={pick} />
        <Button type="button" variant="outline" size="sm" disabled={busy}
                onClick={() => input.current?.click()} data-testid={testId}>
          <Upload className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Uploading…" : preview ? `Replace ${label.toLowerCase()}` : `Upload ${label.toLowerCase()}`}
        </Button>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}
